// react
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import { Box, Button } from '@mui/material';

// api
import { useGetTimestampSummaryQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import AzureStyleDateRangeSelector from '@/components/ui/datetime/AzureStyleDateRangeSelector';

// feature-components
import TimestampsSummaryWarningRenderer from '@/features/energyxplorer/components/timestamps-summary/TimestampsSummaryWarningRenderer';
import { useTimestampSummaryEvaluation } from '@/features/energyxplorer/components/timestamps-summary/useTimestampSummaryEvaluation';

// feature-hooks
import { useDayRangePresets } from '@/features/energyxplorer/hooks/useDayRangePresets';

// feature-utils
import { getRootNode } from '@/features/energyxplorer/utils/energyTreeFunctions';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
// components
import useSearchParams from '@/hooks/useStableSearchParams';
import { useUrlDateRange } from '@/hooks/useUrlDateRange';
import { showSnackbar } from '@/redux/snackbarSlice';
import { getTimestampSummaryValidationStatus } from '@/utils/getTimestampSummaryValidationStatus';

const DateRangeSelector = ({ toKey, fromKey }) => {
  const { t } = useTranslation('energyxplorer');
  const presets = useDayRangePresets();
  const hasManuallyAdjustedOnce = useRef(true);

  const dispatch = useDispatch();

  const { accountId, energyGroupId } = useParams({ strict: false });

  const {
    data: selectedEnergyGroup,
    isLoading: isLoadingEnergyGroup,
    isFetching: isFetchingEnergyGroup,
  } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  //===Time Range & Validity===//
  const {
    currentData: validTimestampSummary,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useGetTimestampSummaryQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
    },
    {
      skip:
        !selectedEnergyGroup?.energyGroupId ||
        !accountId ||
        selectedEnergyGroup?.energyTreeNodes?.length > 250,
    },
  );

  // From hook:
  // - from, to: Luxon DateTime (in zone)
  // - range: [from, to]
  // - timeRange: { start, end } in UTC millis
  const { from, to, setRange, timeRange } = useUrlDateRange({
    zone: 'Europe/Copenhagen',
    defaultDays: 30,
    toKey,
    fromKey,
  });

  const rootNode = useMemo(() => {
    if (!selectedEnergyGroup?.energyTreeNodes?.length) return null;
    return getRootNode(selectedEnergyGroup.energyTreeNodes);
  }, [selectedEnergyGroup]);

  const treeNodesLite = useMemo(() => {
    const treeNodes = selectedEnergyGroup?.energyTreeNodes || [];
    return treeNodes.map((treeNode) => ({
      energyTreeNodeId: String(treeNode.energyTreeNodeId),
      parentId: treeNode.parentId === null ? null : String(treeNode.parentId),
      type: treeNode.type,
    }));
  }, [selectedEnergyGroup]);

  const {
    displayWarning: showWarningBadge,
    adjustedRange,
    isSummaryInverted,
    isSummaryNoData,
  } = useTimestampSummaryEvaluation({
    accountId: Number.isFinite(Number(accountId)) ? Number(accountId) : null,
    energyGroupId: Number.isFinite(Number(energyGroupId))
      ? Number(energyGroupId)
      : null,
    scope: 'node',
    compareRange: timeRange,
    treeNodes: treeNodesLite,
    nodeId: rootNode?.energyTreeNodeId
      ? String(rootNode.energyTreeNodeId)
      : null,
    includeAllChildrenInWarning: true,
  });

  // "Adjust time range" is only meaningful when the evaluation produced a
  // usable snap-to-valid-range suggestion (not inverted/no-data).
  const canAdjustToValidRange =
    showWarningBadge &&
    !isSummaryInverted &&
    !isSummaryNoData &&
    !!adjustedRange &&
    !isLoadingSummary &&
    !isFetchingSummary &&
    !isLoadingEnergyGroup &&
    !isFetchingEnergyGroup;

  const handleAdjustToValidRange = () => {
    if (!adjustedRange) return;

    const { start: adjustedStart, end: adjustedEnd } = adjustedRange;
    if (!adjustedStart || !adjustedEnd) return;

    setRange(adjustedStart, adjustedEnd, {
      // Keep exact evaluated bounds; day rounding can re-introduce out-of-bounds.
      roundToDay: false,
      clampToNow: true,
    });
  };

  useAlertProvider({
    enabled: canAdjustToValidRange,
    severity: 'warning',
    message: t('common.timestamp_summary_warning', { ns: 'energyxplorer' }),
    action: (
      <Button color="inherit" size="small" onClick={handleAdjustToValidRange}>
        {t('daterange.adjust_period', { ns: 'energyxplorer' })}
      </Button>
    ),
  });

  const validation = useMemo(() => {
    return getTimestampSummaryValidationStatus(
      timeRange,
      validTimestampSummary,
    );
  }, [timeRange, validTimestampSummary]);

  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const isRangeEmpty = !fromParam && !toParam;
  useEffect(() => {
    if (!accountId || !energyGroupId) return;
    if (hasManuallyAdjustedOnce.current && !isRangeEmpty) return; // ✅ skip after first adjustment

    if (validation.isInverted) {
      hasManuallyAdjustedOnce.current = true;
      return;
    }

    if (validation.isCompletelyInside) {
      hasManuallyAdjustedOnce.current = true;
      return;
    }
    if (validation.isCompletelyOutside) {
      setRange(
        validation.minLatest.minus({ weeks: 4 }).startOf('day'),
        validation.minLatest,
        {
          roundToDay: true,
          clampToNow: true,
        },
        { replace: true },
      );

      hasManuallyAdjustedOnce.current = true;
      dispatch(
        showSnackbar({
          message: t('daterange.range_adjusted_to_valid_data'),
          severity: 'info',
        }),
      );
      return;
    }

    if (validation.isOutOfBounds && !validation.isCompletelyOutside) {
      hasManuallyAdjustedOnce.current = true;
      setRange(
        validation.adjustedRange[1].minus({ weeks: 4 }).startOf('day'),
        validation.adjustedRange[1],
        {
          roundToDay: true,
          clampToNow: true,
        },
        { replace: true },
      );
      dispatch(
        showSnackbar({
          message: t('daterange.end_adjusted_to_valid_data'),
          severity: 'info',
        }),
      );
    }
  }, [
    dispatch,
    setRange,
    validation,
    accountId,
    energyGroupId,
    t,
    isRangeEmpty,
  ]);

  useEffect(() => {
    if (!accountId || !energyGroupId) return;
    hasManuallyAdjustedOnce.current = false;
  }, [accountId, energyGroupId]);

  return (
    <AzureStyleDateRangeSelector
      from={from}
      to={to}
      presets={presets}
      includeTime={false}
      disableFuture
      showWarning={showWarningBadge}
      onRangeChange={({ from: nextFrom, to: nextTo }) =>
        setRange(nextFrom, nextTo, { roundToDay: true, clampToNow: true })
      }
    >
      {rootNode && (
        <Box sx={{ mt: 2 }}>
          <TimestampsSummaryWarningRenderer
            variant="node"
            energyGroup={selectedEnergyGroup}
            node={rootNode}
            includeAllChildrenInWarning={true}
          />
        </Box>
      )}
    </AzureStyleDateRangeSelector>
  );
};

export default DateRangeSelector;
